const GA4_MEASUREMENT_ID = import.meta.env.VITE_GA4_MEASUREMENT_ID as string | undefined;
const GOOGLE_ADS_ID = import.meta.env.VITE_GOOGLE_ADS_ID as string | undefined;
const GOOGLE_ADS_CONVERSION_LABEL = import.meta.env.VITE_GOOGLE_ADS_CONVERSION_LABEL as
  | string
  | undefined;

let ga4Initialized = false;

export function initGa4ConsentDefaults(): void {
  if (!GA4_MEASUREMENT_ID && !GOOGLE_ADS_ID) return;

  window.dataLayer = window.dataLayer ?? [];
  // Must use `arguments` (not rest params) — gtag.js expects Arguments objects
  // in the dataLayer, not Arrays. Array.isArray(arguments) === false, which is
  // how gtag.js distinguishes commands from plain data-layer pushes.
  window.gtag = function gtag() {
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion, prefer-rest-params -- guaranteed by line above; gtag.js requires Arguments object
    window.dataLayer!.push(arguments);
  } as unknown as Gtag;

  window.gtag('consent', 'default', {
    analytics_storage: 'denied',
    ad_storage: 'denied',
    ad_user_data: 'denied',
    ad_personalization: 'denied',
  });
}

export function loadGa4(): void {
  if ((!GA4_MEASUREMENT_ID && !GOOGLE_ADS_ID) || ga4Initialized) return;
  ga4Initialized = true;

  window.gtag?.('consent', 'update', {
    analytics_storage: 'granted',
    ad_storage: 'granted',
    ad_user_data: 'granted',
    ad_personalization: 'denied',
  });

  // Load gtag.js once — the same script handles both GA4 and Google Ads.
  // Guard already checked that at least one ID exists above.
  const primaryId = (GA4_MEASUREMENT_ID ?? GOOGLE_ADS_ID) as string;
  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${primaryId}`;
  document.head.appendChild(script);

  window.gtag?.('js', new Date());

  if (GA4_MEASUREMENT_ID) {
    window.gtag?.('config', GA4_MEASUREMENT_ID, {
      send_page_view: false,
    });
  }

  if (GOOGLE_ADS_ID) {
    window.gtag?.('config', GOOGLE_ADS_ID);
  }
}

export function revokeGa4Consent(): void {
  window.gtag?.('consent', 'update', {
    analytics_storage: 'denied',
    ad_storage: 'denied',
    ad_user_data: 'denied',
  });
  ga4Initialized = false;
}

export function sendGa4PageView(path: string, title: string): void {
  if (!ga4Initialized) return;
  window.gtag?.('event', 'page_view', {
    page_path: path,
    page_title: title,
  });
}

export function sendGa4Event(eventName: string, params?: Record<string, unknown>): void {
  if (!ga4Initialized) return;
  window.gtag?.('event', eventName, params);
}

export function sendGoogleAdsConversion(params?: {
  transaction_id?: string;
  value?: number;
  currency?: string;
}): void {
  if (!GOOGLE_ADS_ID || !ga4Initialized) return;
  const sendTo = GOOGLE_ADS_CONVERSION_LABEL
    ? `${GOOGLE_ADS_ID}/${GOOGLE_ADS_CONVERSION_LABEL}`
    : GOOGLE_ADS_ID;
  window.gtag?.('event', 'conversion', {
    send_to: sendTo,
    ...(params?.transaction_id ? { transaction_id: params.transaction_id } : {}),
    ...(params?.value != null ? { value: params.value, currency: params.currency ?? 'USD' } : {}),
  });
}

export function isGa4Initialized(): boolean {
  return ga4Initialized;
}

export function getGoogleAdsId(): string | undefined {
  return GOOGLE_ADS_ID;
}
